import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Gift, Star, Package, Gem, Users, Link2, Share2, Loader2, Sparkles, Trophy, MessageCircle, Award, Ticket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { navigateToRoute } from '@/utils/environment-urls';
import { clientService } from '@/services/client.service';
import { couponsService } from '@/services/coupons.service';
import { EstatisticasCliente } from '@/types/estatisticas-cliente';
import { Button } from '@/components/ui/button';
import CompletarCadastro from '@/components/CompletarCadastro';

const HomeCliente = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [estatisticas, setEstatisticas] = useState<EstatisticasCliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCompletarCadastro, setShowCompletarCadastro] = useState(false);
  const [totalCuponsAtivos, setTotalCuponsAtivos] = useState(0);
  const [campanhasAtivas, setCampanhasAtivas] = useState<any[]>([]);
  const [bannersLoading, setBannersLoading] = useState(false);
  const userData = JSON.parse(localStorage.getItem('userlogged') || '{}');

  useEffect(() => {
    if(userData?.role == 'revendedora' || userData?.role == 'coordenadora') {
      navigate('/home');
      return;
    }
    const token = localStorage.getItem('token');
    if (token && userData && !userData.address) {
      // Se não tem pré-cadastro pendente mas não tem endereço, abre modal
      setShowCompletarCadastro(true);
    }
    loadEstatisticas();
    loadCampanhasAtivas();
  }, []);

  const loadCampanhasAtivas = async () => {
    setBannersLoading(true);
    try {
      const data = await couponsService.getCampanhasAtivas();
      setCampanhasAtivas(data || []);
    } catch (error) {
      console.error('Erro ao carregar campanhas ativas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar banners da campanha',
        variant: 'destructive',
      });
    } finally {
      setBannersLoading(false);
    }
  };

  const loadEstatisticas = async () => {
    if (!userData?.id) {
      toast({
        title: "Erro",
        description: "Usuário não encontrado. Faça login novamente.",
        variant: "destructive",
      });
      navigate('/authentication/side-login');
      return;
    }

    setLoading(true);
    try {
      const [estatisticasData] = await Promise.all([
        clientService.getEstatisticasCliente(userData.id),
      ]);
      setEstatisticas(estatisticasData);
      //setTotalCuponsAtivos(cuponsAtivos);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      // Em caso de erro, mantem os valores zerados para não quebrar a interface
      setEstatisticas({
        diamantes: 0,
        total_vendas: 0,
        indicacoes_nivel_1: 0
      });
    } finally {
      setLoading(false);
    }
  };

  
  const handleCompletarCadastroSuccess = (newUserData: any) => {
    localStorage.setItem('userlogged', JSON.stringify(newUserData));
    setShowCompletarCadastro(false);
  };

  const openWhatsApp = (telefone: string) => {
    const text = `Olá, gostaria de falar com minha revendedora ${estatisticas?.revendedora?.nome} (#${estatisticas?.revendedora?.codigo})`;
    window.open(`https://api.whatsapp.com/send?phone=55${telefone.replace(/\D/g, '')}&text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      {/* Header com saudação */}
      <div className="px-4 lg:px-8 pt-6 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
              Olá, {userData.nome_razao_social.split(" ")[0]}!
            </h1>
            <p className="text-muted-foreground text-sm">
              Bem-vindo(a) à sua área exclusiva
            </p>
          </div>
          <Badge variant="outline" className="text-primary border-primary bg-primary/5">
            Cliente
          </Badge>
        </div>
      </div>
      {/* Banners/Slides */}
      {bannersLoading && (
        <div className="px-4 lg:px-8 mb-4">
          <div className="w-full h-40 lg:h-48 rounded-lg bg-muted animate-pulse" />
        </div>
      )}

      <div className='lg:grid lg:grid-cols-2'>
        {!bannersLoading && campanhasAtivas.length > 0 && (
          <div className="px-4 lg:px-8 mb-6">
            {/* mostrar primeiro banner ativo; clique leva para /meus-cupons */}
            {campanhasAtivas[0]?.banner_url && (
              <div
                className="rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/meus-cupons')}
              >
                <img
                  src={campanhasAtivas[0].banner_url}
                  alt={campanhasAtivas[0].nome || 'Banner campanha'}
                  className="w-full h-48 md:h-[22rem] object-fill md:object-cover rounded-lg shadow-md"
                />
              </div>
            )}
          </div>
        )}

       
        <div>
          {/* Cards de métricas principais */}
          <div className="px-4 lg:px-8 mb-6">
            <div className="grid grid-cols-3 lg:grid-cols-3 gap-3 lg:gap-4">
              <Card className="bg-gradient-to-br from-gold/5 to-gold/10 border-gold/20">
                <CardContent className="p-3 lg:p-4 text-center">
                  <Gem className="w-5 h-5 lg:w-6 lg:h-6 text-purple-800 mx-auto mb-1" />
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-purple-800 mx-auto mb-1" />
                  ) : (
                    <div className="text-lg lg:text-xl font-bold text-purple-800">{estatisticas?.diamantes || 0}</div>
                  )}
                  <div className="text-xs text-muted-foreground">Diamantes</div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-3 lg:p-4 text-center">
                  <Package className="w-5 h-5 lg:w-6 lg:h-6 text-green-600 mx-auto mb-1" />
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-green-600 mx-auto mb-1" />
                  ) : (
                    <div className="text-lg lg:text-xl font-bold text-green-600">{estatisticas?.total_vendas || 0}</div>
                  )}
                  <div className="text-xs text-muted-foreground">Pedidos</div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                <CardContent className="p-3 lg:p-4 text-center">
                  <Share2 className="w-5 h-5 lg:w-6 lg:h-6 text-primary mx-auto mb-1" />
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-primary mx-auto mb-1" />
                  ) : (
                    <div className="text-lg lg:text-xl font-bold text-foreground">{estatisticas?.indicacoes_nivel_1 || 0}</div>
                  )}
                  <div className="text-xs text-muted-foreground">Indicações</div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Navegação rápida */}
          <div className="px-4 lg:px-8 mb-6">
            <div className="grid grid-cols-4 lg:grid-cols-4 gap-2 lg:gap-4">
              <button
                onClick={() => navigate('/minha-rede')}
                className="flex flex-col items-center gap-1 lg:gap-2 p-2 lg:p-3 rounded-xl bg-card hover:bg-accent transition-colors"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
                </div>
                <span className="text-xs lg:text-sm font-medium text-foreground">Minha Rede</span>
              </button>

              <button
                onClick={() => navigateToRoute('/loja-recompensas', true)}
                className="flex flex-col items-center gap-1 lg:gap-2 p-2 lg:p-3 rounded-xl bg-card hover:bg-accent transition-colors"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gold/10 rounded-xl flex items-center justify-center">
                  <Gift className="w-5 h-5 lg:w-6 lg:h-6 text-gold" />
                </div>
                <span className="text-xs lg:text-sm font-medium text-foreground">Loja de Recompensas</span>
              </button>

              <button
                onClick={() => navigate('/pedidos')}
                className="flex flex-col items-center gap-1 lg:gap-2 p-2 lg:p-3 rounded-xl bg-card hover:bg-accent transition-colors"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
                </div>
                <span className="text-xs lg:text-sm font-medium text-foreground">Pedidos</span>
              </button>

              <button
                onClick={() => navigate('/links-indicacao')}
                className="flex flex-col items-center gap-1 lg:gap-2 p-2 lg:p-3 rounded-xl bg-card hover:bg-accent transition-colors"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Link2 className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
                </div>
                <span className="text-xs lg:text-sm font-medium text-foreground">Links de Indicação</span>
              </button>
            </div>
          </div>
        </div>
    


       </div>

      

      {/* Cards de promoções e benefícios */}
      <div className="px-4 lg:px-8 space-y-4 lg:space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Card Meus Cupons - DESTAQUE - Span 2 colunas no desktop */}
          <Card 
            className="lg:col-span-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-400 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/meus-cupons')}
          >
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Badge className="bg-white/20 text-white border-white/30 mb-2 lg:mb-3">
                    🎟️ Campanha do Mês
                  </Badge>
                  <h3 className="text-lg lg:text-xl font-bold mb-1 lg:mb-2">Confira seus Cupons da Sorte</h3>
                  <div className="text-white/90 text-sm lg:text-base">
                    A cada R$100 em compras ou indicações com compras, você ganha cupons para os sorteios semanais e a Super Live!
                  </div>
                  <button className="text-white text-xs lg:text-sm underline mt-2 lg:mt-3 font-medium">
                    Ver meus cupons →
                  </button>
                </div>
                <Ticket className="w-12 h-12 lg:w-16 lg:h-16 text-white/60 flex-shrink-0 ml-4" />
              </div>
            </CardContent>
          </Card>

          {/* Programa de benefícios - Sidebar */}
          <Card className="bg-gradient-to-r from-primary to-primary-hover border-primary/20 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
           onClick={()=>{
              navigateToRoute('/loja-recompensas', true);
            }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex flex-col items-start h-full">
                <Badge className="bg-white/20 text-white border-white/30 mb-2 lg:mb-3">
                  Benefícios
                </Badge>
                <div className="text-white text-sm lg:text-base flex-1">
                  Acumule diamantes com suas compras e indicações e resgate produtos incríveis!
                </div>
                <Gem className="w-8 h-8 lg:w-12 lg:h-12 text-white/60 mt-auto" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Indique e ganhe */}
          <Card className="bg-gradient-to-r from-secondary to-accent border-secondary/20 cursor-pointer hover:shadow-lg transition-shadow"
           onClick={()=>{
            navigateToRoute('/minha-rede', true);
          }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Badge className="bg-primary/10 text-primary border-primary/20 mb-2 lg:mb-3">
                    Indique e ganhe
                  </Badge>
                  <div className="text-foreground text-xs lg:text-sm">
                    <b>Convide amigos(as) para serem clientes e receba +15 diamantes de bônus.</b><br className="hidden lg:block"/><br className="hidden lg:block"/>
                    Você também pode indicar para ser Revendedora X. Acumule diamantes em até 3 níveis!
                  </div>
                </div>
                <Gift className="w-8 h-8 lg:w-12 lg:h-12 text-primary/60 flex-shrink-0 ml-4" />
              </div>
            </CardContent>
          </Card>

          {/* Mantenha benefícios ativos */}
          <Card className="bg-gradient-to-r from-purple/10 to-purple-400 cursor-pointer hover:shadow-lg transition-shadow">
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Badge className="bg-purple/10 text-primary border-primary/20 mb-2 lg:mb-3">
                    Mantenha ativo!
                  </Badge>
                  <div className="text-foreground text-xs lg:text-sm">
                    <b>Realize ao menos R$100 em compras no mês para garantir seus diamantes acumulados pela rede.</b><br className="hidden lg:block"/><br className="hidden lg:block"/>
                    Na virada do mês, os diamantes são creditados automaticamente!
                  </div>
                </div>
                <Award className="w-8 h-8 lg:w-12 lg:h-12 text-primary/60 flex-shrink-0 ml-4" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Novidades - Full width */}
        <Card className="bg-gradient-to-r from-gold-light to-gold border-gold-light cursor-pointer hover:shadow-lg transition-shadow">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <Badge className="bg-gold-light text-gold-dark border-gold mb-2 lg:mb-3">
                  Novidades
                </Badge>
                <h3 className="text-foreground font-semibold text-sm lg:text-base mb-1 lg:mb-2">
                  Confira os novos produtos!
                </h3>
                <div className="text-foreground text-sm mb-3">Fale com sua revendedora e consulte nossos lançamentos</div>

                {estatisticas?.revendedora == null && (
                  <div className="text-sm text-muted-foreground">
                    Ainda não possui uma revendedora? Fale conosco pelo menu lateral na opção "Suporte"
                  </div>
                )}
                {estatisticas?.revendedora && (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200 mt-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500 rounded-full">
                        <MessageCircle className="w-4 h-4 text-white" />
                      </div>
                      <div>
                          <span className="font-medium text-green-700 text-sm">{estatisticas?.revendedora?.nome}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={()=> openWhatsApp(estatisticas?.revendedora?.telefone)}
                      className="bg-green-500 hover:bg-green-600 text-xs lg:text-sm"
                    >                     
                      Contato
                    </Button>
                  </div>
                )}
              </div>
              <Sparkles className="w-8 h-8 lg:w-12 lg:h-12 text-gold-dark flex-shrink-0 ml-4" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Seção de informações */}
      <div className="px-4 lg:px-8 py-6 lg:py-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Como funciona
          </h2>
        </div>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">1</span>
              </div>
              <div>
                <h4 className="font-medium text-sm text-foreground">Faça suas compras</h4>
                <p className="text-xs text-muted-foreground">Compre através de uma revendedora e acumule diamantes</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">2</span>
              </div>
              <div>
                <h4 className="font-medium text-sm text-foreground">Convide amigos</h4>
                <p className="text-xs text-muted-foreground">Indique outras pessoas e ganhe diamantes extras</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">3</span>
              </div>
              <div>
                <h4 className="font-medium text-sm text-foreground">Resgate recompensas</h4>
                <p className="text-xs text-muted-foreground">Troque seus diamantes acumulados por itens incríveis na loja de recompensas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Espaçamento para navegação mobile */}
      <div className="h-20 lg:h-0"></div>
      <CompletarCadastro
        isOpen={showCompletarCadastro}
        userData={userData}
        onSuccess={handleCompletarCadastroSuccess}
      />
    </div>
  );
};

export default HomeCliente;
